import { useCallback, useRef, useState } from "react";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import { ArtifactKind, ArtifactMap, getArtifactHeaderName, getArtifactType } from "../utils/artifactUtils";
import { ARTIFACT_TAG_TYPE } from "modules/chat/utils/chatUtils";

/** Returns (and lazily creates) the inner map for a given kind. */
function getOrCreate(map: ArtifactMap, key: ArtifactKind): Map<string, ArtifactProto> {
  let inner = map.get(key);
  if (!inner) {
    inner = new Map();
    map.set(key, inner);
  }
  return inner;
}

/**
 * Creates a shallow clone of the outer map with each inner map also cloned.
 * Used inside setArtifactMap updaters to produce a new reference for React.
 */
function cloneArtifactMap(map: ArtifactMap): ArtifactMap {
  const clone: ArtifactMap = new Map();
  Array.from(map.entries()).forEach(([key, inner]) => {
    clone.set(key, new Map(inner));
  });
  return clone;
}

/**
 * Writes all entries for an artifact into the nested map.
 *
 * Bucket assignments:
 *   DATAFRAME_ANALYSIS  → header name into "code"; each pandas_df → "dataframe";
 *                         each chart → "chart"; each report → "report".
 *   KG_DATAFRAME        → header name into "dataframe".
 *   SCENARIO_JOB        → header name into "workflow".
 *   ANALYSIS_OUTPUT     → header name into "workflow-run".
 *
 * Sub-resources of different analyses can share a name without collision because
 * each bucket's inner map is keyed by name independently of other buckets.
 */
function indexArtifact(artifact: ArtifactProto, map: ArtifactMap): void {
  const name = getArtifactHeaderName(artifact);
  const type = getArtifactType(artifact);

  if (type === ArtifactType.DATAFRAME_ANALYSIS) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.code).set(name, artifact);
    artifact.dataframe_analysis?.pandas_dataframes.forEach(df => {
      if (df.name) getOrCreate(map, ARTIFACT_TAG_TYPE.dataframe).set(df.name, artifact);
    });
    artifact.dataframe_analysis?.charts.forEach(chart => {
      if (chart.name) getOrCreate(map, ARTIFACT_TAG_TYPE.chart).set(chart.name, artifact);
    });
    artifact.dataframe_analysis?.reports.forEach(report => {
      if (report.name) getOrCreate(map, ARTIFACT_TAG_TYPE.report).set(report.name, artifact);
    });
  } else if (type === ArtifactType.KG_DATAFRAME) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.dataframe).set(name, artifact);
  } else if (type === ArtifactType.SCENARIO_JOB) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.workflow).set(name, artifact);
  } else if (type === ArtifactType.ANALYSIS_OUTPUT) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.workflowRun).set(name, artifact);
  }
}

/**
 * Returns the primary type-bucket key for a given artifact type.
 * Used by updateArtifact to check whether the artifact is registered before re-indexing.
 */
function primaryBucket(artifact: ArtifactProto): ArtifactKind {
  const type = getArtifactType(artifact);
  if (type === ArtifactType.DATAFRAME_ANALYSIS) return ARTIFACT_TAG_TYPE.code;
  if (type === ArtifactType.KG_DATAFRAME) return ARTIFACT_TAG_TYPE.dataframe;
  if (type === ArtifactType.SCENARIO_JOB) return ARTIFACT_TAG_TYPE.workflow;
  return ARTIFACT_TAG_TYPE.workflowRun;
}

/**
 * Nested artifact registry — single source of truth for the chat session.
 *
 * `artifactMap` is `Map<ArtifactKind, Map<name, ArtifactProto>>`, mirroring
 * the backend's `map<artifact_type, map<artifact_name, Artifact>>`.  Each type
 * bucket has its own inner map so artifacts of different types can share the same
 * name without collision (e.g. a "code" artifact and a "dataframe" artifact both
 * named "results" coexist independently).
 *
 * `lookupByName` still provides O(n_buckets) lookup — at most 6 Map.get calls —
 * which is effectively O(1) for practical session sizes.
 */
export function useArtifactStore() {
  const [artifactMap, setArtifactMap] = useState<ArtifactMap>(new Map());
  // Always points to the latest map so lookupByName doesn't need artifactMap as a dep.
  const artifactMapRef = useRef(artifactMap);
  artifactMapRef.current = artifactMap;

  /**
   * Register one or more artifacts. Each artifact is indexed into its type bucket.
   * Always overwrites — each call delivers a newer version of the artifact (e.g. a
   * later streaming chunk or a fresh agent run).
   * Deps: [] — helpers are module-level; only the stable React setter is used.
   */
  const registerArtifacts = useCallback((...artifacts: ArtifactProto[]) => {
    setArtifactMap(prev => {
      const next = cloneArtifactMap(prev);
      for (const artifact of artifacts) {
        indexArtifact(artifact, next);
      }
      return next;
    });
  }, []);

  /**
   * Replace an artifact in the map (used after populate()).
   * Re-indexes all sub-resource names to point to the updated artifact.
   * Deps: [] — helpers are module-level; only the stable React setter is used.
   */
  const updateArtifact = useCallback((next: ArtifactProto) => {
    const name = getArtifactHeaderName(next);
    setArtifactMap(prev => {
      if (!prev.get(primaryBucket(next))?.has(name)) return prev;
      const updated = cloneArtifactMap(prev);
      indexArtifact(next, updated);
      return updated;
    });
  }, []);

  /**
   * Look up any artifact or sub-resource by name.
   * When `kind` is provided, searches only that type bucket (O(1)).
   * Without `kind`, searches all buckets sequentially (at most 6 Map.get calls).
   * Returns the ArtifactProto — for sub-resources this is the parent artifact.
   *
   * Deps: [] — reads via artifactMapRef so the reference never changes, preventing
   * useMemo invalidation in every MarkdownRenderer on each artifact registration.
   */
  const lookupByName = useCallback((name: string, kind?: ArtifactKind): ArtifactProto | undefined => {
    if (kind) return artifactMapRef.current.get(kind)?.get(name);
    const buckets = Array.from(artifactMapRef.current.values());
    for (let i = 0; i < buckets.length; i++) {
      const found = buckets[i].get(name);
      if (found) return found;
    }
    return undefined;
  }, []);

  /** Reset the store. */
  const clearStore = useCallback(() => {
    setArtifactMap(new Map());
  }, []);

  return {
    artifactMap,
    registerArtifacts,
    updateArtifact,
    lookupByName,
    clearStore,
  };
}
